import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Loader2, Send } from "lucide-react";

// The five-dimension Biopsychosocial Orientation intake map.
const SECTIONS = [
  {
    title: "Dimension 1 · Body — the physiology of bracing",
    questions: [
      "When you pause right now, where does your body instinctively gather weight or hold itself on guard (e.g., jaw, shoulders, belly, shallow breath)?",
      "What does your body do when it feels an unexpected demand or sudden shift in your day?",
      "How does your physical system let you know it has crossed the line from \"managing\" into pure endurance or depletion?",
      "What does the earliest, smallest signal of ease or physical softening feel like for you, even if it only lasts a moment?",
    ],
  },
  {
    title: "Dimension 2 · Mind — the stories that kept you ready",
    questions: [
      "What is the quiet, familiar sentence your mind repeats to keep you prepared for whatever might happen next?",
      "When you consider setting down a responsibility or asking for space, what does your mind warn you will happen?",
      "In what areas of your life has staying hyper-vigilant or over-prepared felt like the only reasonable way to stay safe or capable?",
      "Where do you find yourself feeling the pressure to perform \"calm\" or \"healing\" rather than just experiencing where you actually are?",
    ],
  },
  {
    title: "Dimension 3 · Relationships — who you learned to be for others",
    questions: [
      "Who did you learn you had to be to keep the emotional weather steady in the rooms you grew up in?",
      "Which role do you most easily slip into around others (the anchor, the problem solver, the quiet one, the steady hand)?",
      "What is something you frequently carry or manage for others that you rarely, if ever, speak out loud?",
      "How does your body physically respond when someone offers you genuine help or attention without conditions?",
    ],
  },
  {
    title: "Dimension 4 · History — where the bracing began",
    questions: [
      "Looking back across your timeline, when was the first season you remember learning that you could not afford to drop your guard?",
      "What adaptations or survival strategies served you well in the past that your body is still using today out of habit?",
      "If your current way of bracing had an original purpose, what was it trying to protect or preserve?",
      "What kind of permission or understanding did you need back then that you never received?",
    ],
  },
  {
    title: "Dimension 5 · Environment — what still asks it of you",
    questions: [
      "What spaces, routines, or expectations in your current daily life continuously ask your nervous system to stay ready?",
      "Where in your home or daily rhythm do you find a pocket of space where nothing is required of you?",
      "What sensory details around you (sounds, pace, light, clutter, demands) tend to quietly tighten your baseline tension through the day?",
      "As you finish this map, what is one thing you hope is recognized and met about your experience without needing to be explained or justified?",
    ],
  },
];

const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.questions);

export default function OrientationIntake({ onSubmitted }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const topRef = useRef(null);

  const setAns = (idx, val) => setAnswers((a) => ({ ...a, [idx]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!fullName.trim()) { setErr("Please share your name so Casey knows who you are."); return; }
    if (!email.trim() || !email.includes("@")) { setErr("A valid email is needed so Casey can reach you."); return; }
    setSending(true);
    try {
      const responses = ALL_QUESTIONS.map((q, i) => ({ question: q, answer: (answers[i] || "").trim() }));
      await axios.post(`${API}/offerings/orientation/intake`, {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        responses,
      });
      onSubmitted?.();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Something interrupted the sending. Please try again.");
    } finally {
      setSending(false);
    }
  };

  let qIndex = -1;
  return (
    <form onSubmit={submit} ref={topRef} className="mt-8 rounded-2xl bg-ivory-50 p-6 shadow-sm ring-1 ring-ivory-400/60 sm:p-8" data-testid="orientation-intake-form">
      <p className="font-display text-xl text-ink">The intake map — in your own time</p>
      <p className="mt-2 text-sm leading-relaxed text-ink-soft">
        A quiet inventory across the five dimensions of the biopsychosocial frame. There are no wrong answers, and no rush — answer only what feels true. Your words are sent only to Casey and become the shape of your first session. Just your name and email are required.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">Full name *</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="orientation-name"
            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">Email *</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="orientation-email"
            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">Phone (optional)</span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="orientation-phone"
            className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
        </label>
      </div>

      {SECTIONS.map((section, si) => (
        <div key={si} className="mt-8">
          <p className="kicker text-terracotta">{section.title}</p>
          <div className="mt-3 space-y-5">
            {section.questions.map((q) => {
              qIndex += 1;
              const idx = qIndex;
              return (
                <label key={idx} className="block">
                  <span className="mb-1.5 block text-sm leading-snug text-ink">
                    <span className="mr-1 text-ink-muted">{idx + 1}.</span>{q}
                  </span>
                  <textarea
                    value={answers[idx] || ""}
                    onChange={(e) => setAns(idx, e.target.value)}
                    rows={2}
                    data-testid={`orientation-q-${idx}`}
                    className="w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50"
                  />
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {err && <p className="mt-5 text-sm text-terracotta" data-testid="orientation-error">{err}</p>}
      <button type="submit" disabled={sending} data-testid="orientation-submit"
        className="debossed-btn mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-ink disabled:opacity-50">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to Casey & continue
      </button>
    </form>
  );
}
